import { useEffect } from 'react';
import { createFormControlRadioOptions } from 'utils';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { filterOptionsByCA } from 'utils/contextActionsUtils';
import { useContextAction } from 'hooks/useContextAction';
import { selectEaiCodebooks } from 'features/eai/selectors';
import { RadioInputOption } from 'models';
import { FormWizardStep } from 'models/form';
import {
  AppModule,
  ContextActions,
  EAIEntityTypeCode,
  InputType,
  ValidationPatterns,
  YupType
} from 'utils/constants';
import { fetchAndSetEaiContextActions } from 'features/eai/thunks';

const {
  CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION,
  CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION_DELIVERY_POINT,
  CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION_BALANCING_GROUP,
  CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_RESERVED_CAPACITY_GUARDING
} = ContextActions.EAI;

const INPUT_SERVICE_CODE = 'ServiceCode';

const getWizardSteps = (
  serviceCodeOptions: RadioInputOption[]
): FormWizardStep[] => [
  {
    label: 'step01',
    children: [
      {
        id: 'DeviceCharacteristicGroupName',
        type: InputType.TEXT_FIELD,
        validation: {
          type: YupType.STRING,
          methods: [
            {
              methodType: 'required',
              args: ['DeviceCharacteristicGroupName-required']
            },
            {
              methodType: 'matches',
              args: [
                ValidationPatterns.STRING_256_MAX_LENGTH,
                'DeviceCharacteristicGroupName-length'
              ]
            }
          ]
        }
      }
    ]
  },
  {
    label: 'step02',
    children: [
      {
        id: INPUT_SERVICE_CODE,
        type: InputType.RADIO_GROUP,
        options: serviceCodeOptions
      }
    ]
  },
  {
    label: 'common.wizard.review',
    children: []
  }
];

// ==============================|| HOOK ||============================== //

const useDeviceCharacteristicGroupWizardSteps = (
  deviceId: string
): FormWizardStep[] => {
  const dispatch = useAppDispatch();
  const eaiCodebooks = useAppSelector(selectEaiCodebooks);

  useEffect(() => {
    if (!deviceId) return;

    const requestBody = {
      resourceEntity: EAIEntityTypeCode.DEVICE,
      identificators: [deviceId],
      contextActions: [
        CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION,
        CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION_DELIVERY_POINT,
        CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION_BALANCING_GROUP,
        CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_RESERVED_CAPACITY_GUARDING
      ]
    };

    dispatch(fetchAndSetEaiContextActions([requestBody]));
  }, [deviceId, dispatch]);

  const { contextAction: caProductionOptimization } = useContextAction({
    actionCode:
      CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION,
    resourceEntity: EAIEntityTypeCode.DEVICE,
    entityIdentificator: deviceId
  });

  const { contextAction: caProductionOptimizationDp } = useContextAction({
    actionCode:
      CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION_DELIVERY_POINT,
    resourceEntity: EAIEntityTypeCode.DEVICE,
    entityIdentificator: deviceId
  });

  const { contextAction: caProductionOptimizationBg } = useContextAction({
    actionCode:
      CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_PRODUCTION_OPTIMIZATION_BALANCING_GROUP,
    resourceEntity: EAIEntityTypeCode.DEVICE,
    entityIdentificator: deviceId
  });

  const { contextAction: caProductionOptimizationReservedCapacityGuarding } =
    useContextAction({
      actionCode:
        CA_EAI_CREATE_DEVICE_CHARACTERISTIC_GROUP_RESERVED_CAPACITY_GUARDING,
      resourceEntity: EAIEntityTypeCode.DEVICE,
      entityIdentificator: deviceId
    });

  const getServiceCodeOptions = () => {
    let options = createFormControlRadioOptions(
      AppModule.EAI,
      INPUT_SERVICE_CODE,
      eaiCodebooks
    );

    options = filterOptionsByCA({
      module: AppModule.EAI,
      inputType: InputType.RADIO_GROUP,
      options,
      value: 'PRODUCTION_OPTIMIZATION',
      contextAction: caProductionOptimization
    }) as RadioInputOption[];

    options = filterOptionsByCA({
      module: AppModule.EAI,
      inputType: InputType.RADIO_GROUP,
      options,
      value: 'PRODUCTION_OPTIMIZATION_DELIVERY_POINT',
      contextAction: caProductionOptimizationDp
    }) as RadioInputOption[];

    options = filterOptionsByCA({
      module: AppModule.EAI,
      inputType: InputType.RADIO_GROUP,
      options,
      value: 'PRODUCTION_OPTIMIZATION_BALANCING_GROUP',
      contextAction: caProductionOptimizationBg
    }) as RadioInputOption[];

    options = filterOptionsByCA({
      module: AppModule.EAI,
      inputType: InputType.RADIO_GROUP,
      options,
      value: 'RESERVED_CAPACITY_GUARDING',
      contextAction: caProductionOptimizationReservedCapacityGuarding
    }) as RadioInputOption[];

    return options;
  };

  return getWizardSteps(getServiceCodeOptions());
};

export default useDeviceCharacteristicGroupWizardSteps;
